import React, { useState, useEffect } from "react";
import { Outlet, useLocation } from "react-router-dom";
import {
  Menu,
  RefreshCw,
  Clock,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import Sidebar from "./Sidebar";
import { useDashboard } from "../context/DashboardContext";
import "./DashboardLayout.css";

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [timeString, setTimeString] = useState("");
  const location = useLocation();
  const { refreshAll, loadingLicenses, loadingUsers, actionMessage } = useDashboard();

  const isRefreshing = loadingLicenses || loadingUsers;

  // Live IST Clock
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const istTime = now.toLocaleTimeString("en-IN", {
        timeZone: "Asia/Kolkata",
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
      setTimeString(istTime);
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // Determine current section name for breadcrumb
  const getCurrentSection = () => {
    const path = location.pathname;
    if (path.includes("/controls")) return "CONTROLS";
    if (path.includes("/licenses")) return "LICENSES";
    return "OVERVIEW";
  };

  return (
    <div className="bf-layout-root">
      {/* Sidebar Navigation */}
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      {/* Main Container */}
      <div className="bf-main-container">
        {/* Topbar HUD */}
        <header className="bf-topbar-hud">
          <div className="bf-topbar-left">
            <button
              className="bf-menu-toggle-btn"
              onClick={() => setSidebarOpen(true)}
              title="Open Navigation Menu"
            >
              <Menu size={18} />
            </button>

            <div className="bf-breadcrumb">
              <span className="bf-breadcrumb-root">CONSOLE</span>
              <span className="bf-breadcrumb-sep">{"//"}</span>
              <span className="bf-breadcrumb-current">{getCurrentSection()}</span>
            </div>
          </div>

          <div className="bf-topbar-right">
            {/* Live IST Clock */}
            <div className="bf-live-clock">
              <Clock size={13} color="var(--text-3)" />
              <span>IST</span>
              <span className="bf-live-clock-time">{timeString || "00:00:00"}</span>
            </div>

            {/* Quick Cluster Re-sync Button */}
            <button
              className={`bf-refresh-btn ${isRefreshing ? "spinning" : ""}`}
              onClick={refreshAll}
              disabled={isRefreshing}
              title="Re-sync data from cluster"
            >
              <RefreshCw size={13} />
              <span>{isRefreshing ? "SYNCING..." : "SYNC"}</span>
            </button>
          </div>
        </header>

        {/* Action Message / Toast */}
        {actionMessage && (
          <div className={`bf-action-toast ${actionMessage.type}`}>
            {actionMessage.type === "success" ? (
              <CheckCircle2 size={16} color="var(--green-0)" />
            ) : (
              <AlertTriangle size={16} color="var(--red)" />
            )}
            <span>{actionMessage.text}</span>
          </div>
        )}

        {/* View Content Body */}
        <main className="bf-page-body">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
